import { Link } from '@tanstack/react-router';
import { billing } from '@lawbee/domain';
import { Alert, Badge, Button, Card, CardContent, cn } from '@lawbee/ui';
import { ExternalLink, Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { hasFeature, useMeQuery } from '../lib/me';
import type { Me } from '../lib/types';

type GateMe = Pick<Me, 'entitlements' | 'hiveWebUrl'> & { workspace: Pick<Me['workspace'], 'managedBy'> };

function featureLabel(feature: string): string {
  return (billing.PLAN_FEATURE_LABELS as Record<string, string>)[feature] ?? feature.replace(/_/g, ' ');
}

/** Where the "upgrade" action goes: Hive billing for Hive-managed workspaces, else our billing page. */
export function UpgradeAction({ me, size = 'md' }: { me: GateMe; size?: 'sm' | 'md' }) {
  if (me.workspace.managedBy === 'hive') {
    const href = me.hiveWebUrl ? `${me.hiveWebUrl.replace(/\/$/, '')}/billing` : undefined;
    return (
      <Button asChild size={size}>
        <a href={href ?? '#'} target="_blank" rel="noreferrer">
          Upgrade in Hive <ExternalLink />
        </a>
      </Button>
    );
  }
  return (
    <Button asChild size={size}>
      <Link to="/settings/billing">Upgrade to Growth</Link>
    </Button>
  );
}

/** Card shown instead of a Growth feature the plan does not include. */
export function UpgradeCard({ feature, me, className }: { feature: string; me: GateMe; className?: string }) {
  const label = featureLabel(feature);
  return (
    <Card className={cn('mx-auto max-w-xl', className)}>
      <CardContent className="flex flex-col items-center gap-4 p-8 text-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-accent text-accent-foreground">
          <Lock className="size-5" aria-hidden />
        </div>
        <div className="space-y-1.5">
          <Badge variant="primary">LawBee Growth</Badge>
          <h2 className="text-xl font-semibold">{label} is part of LawBee Growth</h2>
          <p className="text-sm text-muted-foreground">
            Your firm is on {me.entitlements.planName}.{' '}
            {me.workspace.managedBy === 'hive' ? 'Your plan is managed in Hive.' : `Growth is ${billing.formatPrice(billing.PLANS.law_growth)} per month (CAD).`}
          </p>
        </div>
        <UpgradeAction me={me} />
      </CardContent>
    </Card>
  );
}

/** Render children only when the plan includes `feature`; otherwise the upgrade card. */
export function PlanGate({ feature, children }: { feature: string; children: ReactNode }) {
  const { data: me } = useMeQuery();
  if (!me) return null;
  if (hasFeature(me, feature) || !me.entitlements.active) return <>{children}</>;
  return <UpgradeCard feature={feature} me={me} />;
}

/** Trial countdown and read-only banners at the top of the app. */
export function PlanBanner({ me }: { me: GateMe }) {
  const e = me.entitlements;
  if (!e.active) {
    return (
      <Alert variant="destructive" title="This workspace is read-only" className="mb-6">
        <span className="block">{billing.INACTIVE_MESSAGE}</span>
        <span className="mt-2 block">
          <UpgradeAction me={me} size="sm" />
        </span>
      </Alert>
    );
  }
  if (e.state === 'trialing' && e.trialDaysLeft !== null) {
    return (
      <div role="status" className="mb-6 flex flex-col gap-2 rounded-lg bg-accent px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
        <span>
          <span className="font-medium">Free trial:</span> <span className="figures">{e.trialDaysLeft}</span> {e.trialDaysLeft === 1 ? 'day' : 'days'} left. All Growth features are on during the trial.
        </span>
        {me.workspace.managedBy === 'hive' ? (
          me.hiveWebUrl ? (
            <a className="font-medium text-accent-foreground underline-offset-4 hover:underline" href={`${me.hiveWebUrl.replace(/\/$/, '')}/billing`} target="_blank" rel="noreferrer">
              Choose a plan in Hive
            </a>
          ) : null
        ) : (
          <Link to="/settings/billing" className="font-medium text-accent-foreground underline-offset-4 hover:underline">
            Choose a plan
          </Link>
        )}
      </div>
    );
  }
  if (e.state === 'past_due') {
    return (
      <Alert variant="warning" title="Payment past due" className="mb-6">
        Update your payment method to keep LawBee running.
      </Alert>
    );
  }
  return null;
}
